import { Button } from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import SendIcon from "@mui/icons-material/Send";
import { messagesApi } from "../../API/messagesApi";
import { formatLocalDate, formatLocalTime } from "../../utils/formatDateTime";

// Utility: Group messages by date and insert date stripes (reference: ChatInterface.jsx)
function groupMessagesWithDateStripes(
  messages: Message[]
): Array<
  { type: "date-stripe"; date: Date } | (Message & { type: "message" })
> {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  // Sort messages by timestamp ascending
  const sorted = [...messages].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
  const result: Array<
    { type: "date-stripe"; date: Date } | (Message & { type: "message" })
  > = [];
  let lastDate: string | null = null;
  sorted.forEach((msg) => {
    const msgDate = new Date(msg.timestamp);
    // Format as yyyy-MM-dd for comparison
    const dateKey =
      msgDate.getFullYear() +
      "-" +
      String(msgDate.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(msgDate.getDate()).padStart(2, "0");
    if (dateKey !== lastDate) {
      result.push({ type: "date-stripe", date: msgDate });
      lastDate = dateKey;
    }
    result.push({ type: "message", ...msg });
  });
  return result;
}

interface Message {
  id: string | number;
  body: string;
  direction: "outbound" | "inbound" | string;
  timestamp: string;
  status: string;
}

const Messages = ({
  config,
  auth,
  onStateChange,
  onLogout,
  sendMessageToParent,
  fetchCreditBalance,
  showCredits,
  setShowPurchaseModal,
  getConsumerToken,
  makeConsumerRequest,
  showSnackbar,
  onMessageSent,
}: {
  config: any;
  auth: any;
  onStateChange: (state: string) => void;
  onLogout: () => void;
  sendMessageToParent: (type: string, detail?: any) => void;
  fetchCreditBalance: (auth: any, token: string) => Promise<void>;
  showCredits: number;
  setShowPurchaseModal: (show: boolean) => void;
  getConsumerToken?: () => string | null;
  makeConsumerRequest?: (url: string, options?: RequestInit) => Promise<Response>;
  showSnackbar?: (message: string, severity: "success" | "error" | "warning" | "info") => void;
  onMessageSent?: (conversationId: number | null) => void;
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [conversationId, setConversationId] = useState<number | null>(null);

  console.log("Show credits in message:", showCredits);

  useEffect(() => {
    loadMessages();
    // Mark messages as read when component mounts (reset notification count)
    markMessagesAsRead();
  }, [conversationId, auth]);

  // Listen for new messages from Widget.tsx (which handles SignalR globally)
  useEffect(() => {
    const handleNewOperatorMessage = (event: CustomEvent) => {
      console.log('[Consumer Messages] 📨 Received operator message via SignalR:', event.detail);
      
      const messageData = event.detail;
      
      // OPTIMIZATION: Append message directly to state instead of REST reload
      // Use full message body (messageBody) if available, fallback to preview
      if (messageData && (messageData.messageBody || messageData.messagePreview)) {
        const newMessage: Message = {
          id: `signalr-${Date.now()}`, // Temporary ID until next full sync
          body: messageData.messageBody || messageData.messagePreview, // Use full body, not preview
          direction: 'outbound', // Operator messages are outbound (from operator to consumer)
          timestamp: messageData.createdAt || new Date().toISOString(),
          status: 'delivered'
        };
        
        // Check for duplicate (avoid adding same message twice)
        setMessages(prev => {
          const isDuplicate = prev.some(m => 
            m.body === newMessage.body && 
            Math.abs(new Date(m.timestamp).getTime() - new Date(newMessage.timestamp).getTime()) < 5000
          );
          
          if (isDuplicate) {
            console.log('[Consumer Messages] 🚫 Duplicate message detected, skipping');
            return prev;
          }
          
          console.log('[Consumer Messages] ✅ Appending new message to state');
          return [...prev, newMessage];
        });
        
        // Reset notification count since user is already viewing this tab
        // Dispatch event directly here instead of calling markMessagesAsRead (function not yet defined)
        window.dispatchEvent(new CustomEvent('messages-marked-as-read'));
        
        // Update credits in background (operator response deducts credit)
        const token = getConsumerToken?.() || localStorage.getItem("consumerToken");
        if (token) {
          fetchCreditBalance(auth, token);
        }
      } else {
        // Fallback: If no message data, reload via REST
        console.log('[Consumer Messages] ⚠️ No message data in event, falling back to REST reload');
        loadMessages();
      }
    };

    // Listen for the custom event dispatched by Widget.tsx
    window.addEventListener('operator-message-received', handleNewOperatorMessage as EventListener);
    
    return () => {
      window.removeEventListener('operator-message-received', handleNewOperatorMessage as EventListener);
    };
  }, [auth, getConsumerToken, fetchCreditBalance]);

  // Function to mark messages as read (reset notification count)
  const markMessagesAsRead = () => {
    console.log('[Consumer Messages] 📖 Marking messages as read - resetting notification count');
    // Dispatch custom event to Widget.tsx to reset notification count
    window.dispatchEvent(new CustomEvent('messages-marked-as-read'));
  };

  const loadMessages = async () => {
    try {
      const token = getConsumerToken?.() || localStorage.getItem("consumerToken");
      const response = makeConsumerRequest ?
        await makeConsumerRequest("/api/consumer/messages?limit=20") :
        await fetch("/api/consumer/messages?limit=20", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

      if (!response.ok) {
        throw new Error("Failed to load messages");
      }

      const data = await response.json();
      console.log("message data", data.messages);
      setMessages(data.messages || []);
      // Try to set conversationId from the first message
      if (
        data.messages &&
        data.messages.length > 0 &&
        data.messages[0].conversationId
      ) {
        setConversationId(data.messages[0].conversationId);
      }
      // Fetch latest credits after loading messages
      if (token) {
        fetchCreditBalance(auth, token);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load messages");
    } finally {
      setLoading(false);
    }
  };
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  // Scroll to bottom of messages container only (not the entire parent page)
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendClick = async () => {
    if (!newMessage.trim()) return;

    if (showCredits <= 0) {
      showSnackbar?.("Insufficient credits. Please purchase more credits to send messages.", "error");
      return;
    }

    const maxLength = config?.features?.maxMessageLength || 320;
    if (newMessage.length > maxLength) {
      showSnackbar?.(`Message too long. Maximum ${maxLength} characters allowed.`, "error");
      return;
    }

    // Check if consumer is authenticated
    if (!auth?.consumerId || !auth?.isAuthenticated) {
      showSnackbar?.("You must be logged in to send messages.", "error");
      return;
    }

    setSending(true);
    setError("");

    try {
      console.log("🚀 Sending message...", {
        conversationId,
        message: newMessage.trim(),
      });

      // Debug token retrieval
      const token = getConsumerToken?.() || localStorage.getItem("consumerToken");
      console.log("🔑 Token for message sending:", token ? "Token found" : "No token found");
      console.log("🏢 Client ID:", config?.clientId);

      // Always use REST API to send message
      let data;
      if (!conversationId) {
        // Start new conversation via ConsumerController (client-scoped)
        const response = makeConsumerRequest ?
          await makeConsumerRequest("/api/consumer/messages", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              Content: newMessage.trim(),
            }),
          }) :
          await fetch("/api/consumer/messages", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              "X-Client-Id": config?.clientId?.toString() || "",
            },
            body: JSON.stringify({
              Content: newMessage.trim(),
            }),
          });
        
        console.log("📡 Response status:", response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.log("❌ Response error:", errorText);
          throw new Error(`Failed to send message: ${response.status} ${errorText}`);
        }
        
        data = await response.json();
        if (data.messageId) {
          setConversationId(data.conversationId || null);
        }
      } else {
        // Use MessagesController for existing conversations
        const consumerMessageData = {
          ConversationId: conversationId,
          Body: newMessage.trim(),
          ConsumerId: auth.consumerId,
          ClientId: auth.clientId,
        };

        console.log("🔧 Consumer message data being sent:", consumerMessageData);
        data = await messagesApi.sendConsumerMessage(consumerMessageData, getConsumerToken, config);
      }

      
      if (data.error) {
        throw new Error(data.error || "Failed to send message");
      }

      console.log("✅ Message sent via API:", data);

      // Set conversationId from response for future use
      if (data.conversationId) {
        setConversationId(data.conversationId);
      }

      // Add message to local state
      setMessages((prev) => [
        ...prev,
        {
          id: data.messageId || Date.now(),
          body: newMessage.trim(),
          direction: "inbound",
          timestamp: new Date().toISOString(),
          status: "sent",
        },
      ]);

      setNewMessage("");
      
      // Show success notification
      showSnackbar?.("Message sent successfully!", "success");

      // CRITICAL: Reset internal unread count when consumer sends a message
      // This ensures the badge disappears after replying
      markMessagesAsRead();
      
      // Notify widget that user sent a message (managed globally now)
      sendMessageToParent("sbw-unread-count", { count: 0 });

      // Notify parent (Widget) that message was sent - triggers ack status check
      // This allows immediate banner display if client already has unreplied messages > 5 min
      const finalConversationId = data.conversationId || conversationId;
      onMessageSent?.(finalConversationId);
    } catch (err: any) {
      console.error("❌ Error sending message:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to send message";
      setError(errorMessage);
      showSnackbar?.(errorMessage, "error");
    } finally {
      setSending(false);
    }
  };  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSendClick();
  };

  const maxLength = config?.features?.maxMessageLength || 320;

  if (loading) {
    return (
      <div className="widget-loading">
        <div className="loading-spinner"></div>
        <p>Loading messages...</p>
      </div>
    );
  }

  return (
    <div>
      {error && <div className="error-message">{error}</div>}

      {/* SignalR Connection Status */}
      {/* <div className={`connection-status ${isSignalRConnected ? 'connected' : 'disconnected'}`}>
        <span className="status-indicator"></span>
        {isSignalRConnected ? 'Real-time messaging connected' : 'SignalR not connected - using fallback mode'}
      </div> */}

      <div className="messages-container" ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <p className="text-center">No messages yet. Start a conversation!</p>
        ) : (
          <>
            {groupMessagesWithDateStripes(messages).map((item) => {
              if (item.type === "date-stripe") {
                const today = new Date();
                const isToday =
                  item.date.toDateString() === today.toDateString();
                const isYesterday =
                  item.date.toDateString() ===
                  new Date(today.getTime() - 86400000).toDateString();
                let label = formatLocalDate(item.date);
                if (isToday) label = "Today";
                else if (isYesterday) label = "Yesterday";
                return (
                  <div
                    key={`date-stripe-${formatLocalDate(item.date)}`}
                    style={{ textAlign: "center", margin: "16px 0" }}
                  >
                    <span
                      style={{
                        background: "#e0e0e0",
                        padding: "2px 16px",
                        borderRadius: 8,
                        fontWeight: 600,
                        color: "#555",
                        fontSize: 13,
                      }}
                    >
                      {label}
                    </span>
                  </div>
                );
              } else if (item.type === "message") {
                return (
                  <div key={item.id} className={`message ${item.direction}`}>
                    <div style={{ whiteSpace: "pre-wrap" }}>{item.body}</div>
                    <div className="message-time">
                      {formatLocalTime(item.timestamp)}
                    </div>
                  </div>
                );
              } else {
                return null;
              }
            })}
            <div ref={messagesEndRef} />
          </>
        )}

        {/* Low credit warning (1-4 credits remaining) */}
        {showCredits > 0 && showCredits < 5 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginTop: "10px",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "300px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                background: "#fdeaea",
                padding: "15px",
                borderRadius: "8px",
                border: "1px solid #ab2c29",
              }}
            >
              <h5 style={{ margin: "0px", color: "#ab2c29", fontWeight: "bold" }}>Your remaining credit is running low</h5>
              <p style={{ fontSize: "12px", textAlign: "center", color: "#6b1a18", fontWeight: "normal" }}>
                Use the below button to top up
              </p>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setShowPurchaseModal(true)}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#ab2c29",
                  "&:hover": { backgroundColor: "#8b2321" },
                }}
              >
                Purchase Credit
              </Button>
            </div>
          </div>
        )}

        {/* No credit message (0 credits) */}
        {showCredits <= 0 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "300px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                background: "#f5f5f5",
                padding: "20px",
                borderRadius: "8px",
              }}
            >
              <h5 style={{ margin: "0px" }}>You have no remaining credit</h5>
              <p style={{ fontSize: "12px", textAlign: "center" }}>
                To receive messages, please add <br />
                more credit to your wallet
              </p>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setShowPurchaseModal(true)}
                sx={{
                  textTransform: "none",
                  backgroundColor: "var(--color-primary)",
                  "&:hover": { backgroundColor: "var(--color-secondary)" },
                }}
              >
                Purchase Credit
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="message-input">
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          maxLength={maxLength}
          disabled={sending || showCredits <= 0}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendClick();
            }
          }}
        />
        <div
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            cursor:
              sending || !newMessage.trim() || showCredits <= 0
                ? "not-allowed"
                : "pointer",
            opacity:
              sending || !newMessage.trim() || showCredits <= 0 ? 0.5 : 1,
          }}
          onClick={handleSendClick}
        >
          <SendIcon
            style={{
              transform: "rotate(320deg)",
              color:
                sending || !newMessage.trim() || showCredits <= 0
                  ? "#ccc"
                  : "#007bff",
            }}
          />
        </div>
        <span
          style={{
            position: "absolute",
            right: "50px",
            bottom: "8px",
            fontSize: "11px",
            color: "#888",
          }}
        >
          {newMessage.length}/{maxLength}
        </span>
      </div>
    </div>
  );
};

export default Messages;
